/**
 * GDPR cookie consent utility.
 *
 * Any code that wants to load analytics or marketing scripts MUST first call
 * `hasConsentFor('analytics' | 'marketing')` and should subscribe to
 * `onConsentChange` to react when the user updates their preferences.
 *
 * The consent choice itself is stored in a first-party cookie
 * (`perlla_cookie_consent`) — this is allowed as a strictly-necessary cookie.
 */

export type ConsentCategory = "necessary" | "analytics" | "marketing";

export interface ConsentState {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  version: number;
  timestamp: string;
}

const COOKIE_NAME = "perlla_cookie_consent";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 12 months
/**
 * Bump this integer whenever the set of cookie categories or their meaning
 * changes. Any stored consent with a lower version is treated as
 * not-consented, so the banner re-appears and the user is asked again.
 */
const CONSENT_VERSION = 1;


const CHANGE_EVENT = "perlla:consent-change";
const OPEN_PREFS_EVENT = "perlla:open-cookie-preferences";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.substring(name.length + 1)) : null;
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

export function getConsent(): ConsentState | null {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      version: typeof parsed.version === "number" ? parsed.version : 0,
      timestamp: typeof parsed.timestamp === "string" ? parsed.timestamp : "",
    };
  } catch {
    return null;
  }
}

export function hasConsented(): boolean {
  const c = getConsent();
  return !!c && c.version === CONSENT_VERSION;
}

export function hasConsentFor(category: ConsentCategory): boolean {
  if (category === "necessary") return true;
  // Stale-version consent is treated as no consent for optional categories,
  // so a version bump immediately disables analytics/marketing until the user
  // reconfirms.
  if (!hasConsented()) return false;
  const c = getConsent();
  if (!c) return false;
  return Boolean(c[category]);
}


export function setConsent(next: { analytics: boolean; marketing: boolean }) {
  const state: ConsentState = {
    necessary: true,
    analytics: next.analytics,
    marketing: next.marketing,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };
  writeCookie(COOKIE_NAME, JSON.stringify(state));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: state }));
  }
}

export function acceptAll() {
  setConsent({ analytics: true, marketing: true });
}

export function rejectNonEssential() {
  setConsent({ analytics: false, marketing: false });
}

export function clearConsent() {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: null }));
  }
}

export function onConsentChange(
  cb: (state: ConsentState | null) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (e: Event) => {
    const detail = (e as CustomEvent).detail as ConsentState | null;
    cb(detail);
  };
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/** Trigger the CookieConsentBanner to open the preferences dialog. */
export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_PREFS_EVENT));
}

export const CONSENT_EVENTS = {
  change: CHANGE_EVENT,
  openPreferences: OPEN_PREFS_EVENT,
};
